import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  AuxiliaryInfo,
  CreatedRequestDetails,
  Session,
  UsersInSession,
} from '@helios/state-management'
import { ERROR_INTERCEPTOR_BYPASS } from '@helios/utils'
import { formatDate, ISODateFormat, parseDate } from '@northfork/basics'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

export interface SessionCreationParams {
  accountIds: number[]
  reconDate: string //ISODateFormat
  loadWindowStartDate: string //ISODateFormat
  loadWithBasis?: string
  loadWithDefaultBasis?: boolean
}

interface AuxiliaryInfoResponse {
  LOAD_WINDOW_START_DATE: string
  BASIS_NAME?: string
  BASIS_ID?: number
  NEED_REVIEW?: boolean
}

const BASE_URL = `helios-sessions`
export const RECON_DATE_URL = `${BASE_URL}/currentReconDate`
export const SESSIONS_URL = BASE_URL
export const USERS_IN_SESSION_URL = SESSIONS_URL
export const AUXILLARY_INFO_SESSION_URL = SESSIONS_URL
const isHPLAccounts = `${SESSIONS_URL}/isHPLAccounts`

/**
 * @deprecated - HttpSessionsService should only be used within the SessionsService
 *
 * The SessionsService wraps all of these http calls with appropriate ngrx store updates and logic that determines
 * what toasts and modals to display to the user. Using this service directly could cause important side effects to be
 * missed.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpSessionsService {
  constructor(private readonly http: HttpClient) {}

  /** @internal */
  createSession(
    accountIds: number[],
    reconDate: Date,
    loadWindowStartDate: Date,
    loadWithBasis?: string,
    loadWithDefaultBasis?: boolean,
  ): Observable<Session> {
    return this.http.post<Session>(
      SESSIONS_URL,
      {
        accountIds,
        reconDate: formatDate(reconDate, ISODateFormat),
        loadWindowStartDate: formatDate(loadWindowStartDate, ISODateFormat),
        loadWithBasis,
        loadWithDefaultBasis,
      } as SessionCreationParams,
      { context: new HttpContext().set(ERROR_INTERCEPTOR_BYPASS, [400]) },
    )
  }

  /** @internal */
  loadSessions(): Observable<Session[]> {
    return this.http.get<Session[]>(SESSIONS_URL, {
      context: new HttpContext().set(ERROR_INTERCEPTOR_BYPASS, [403]),
    })
  }

  /** @internal */
  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${SESSIONS_URL}/${sessionId}`)
  }

  /** @internal */
  getUsersInSession(sessionId: string): Observable<UsersInSession[]> {
    return this.http
      .get<{ [key: number]: string }>(
        `${USERS_IN_SESSION_URL}/${sessionId}/users`,
      )
      .pipe(
        map(users => {
          const results: UsersInSession[] = []

          for (const key in users) {
            results.push({
              userId: key,
              username: users[key],
            })
          }

          return results
        }),
      )
  }
  /** @internal */
  getAuxillaryInfo(sessionId: string): Observable<AuxiliaryInfo> {
    return this.http
      .get<AuxiliaryInfoResponse>(
        `${AUXILLARY_INFO_SESSION_URL}/${sessionId}/auxiliary`,
      )
      .pipe(
        map(resp => {
          const dateStr = resp.LOAD_WINDOW_START_DATE
          const basisName = resp.BASIS_NAME
          const basisId = resp.BASIS_ID
          const needsReview = resp.NEED_REVIEW
          return {
            LOAD_WINDOW_START_DATE: parseDate(dateStr, ISODateFormat),
            BASIS_NAME: basisName,
            BASIS_ID: basisId,
            NEED_REVIEW: needsReview,
          }
        }),
      )
  }

  /** @internal */
  getRestrictedRequestIds(
    sessionId: string,
  ): Observable<CreatedRequestDetails[]> {
    return this.http.get<CreatedRequestDetails[]>(
      `${SESSIONS_URL}/${sessionId}/restricted-request-ids`,
    )
  }

  /** @internal */
  getReconDate(): Promise<Date> {
    return this.http
      .get(RECON_DATE_URL, { responseType: 'text' })
      .pipe(map(dateStr => parseDate(dateStr, ISODateFormat)))
      .toPromise()
  }

  /**
   * getIsHplAccount for a specific account id
   *
   * @internal
   */
  getIsHplAccount(accountId: number) {
    return this.http.post<{ [key: string]: boolean }>(isHPLAccounts, [
      accountId,
    ])
  }
}
